import { postLocalJson } from "./localJsonTransport";

/**
 * Can 80: the rail's Tidy up, through the Desk's one tidy (the same one Cleo's
 * plan_session_tidy and archive_quiet_sessions use).
 */
export interface SessionTidyWords {
  readonly title: string;
  readonly lines: ReadonlyArray<string>;
}
/** One tidied session, as the Desk's tidy answers it (@cleo/shared/sessionTidy TidyItem). */
export interface SessionTidyItem {
  readonly sessionId: string;
  readonly label: string;
  readonly roomId: string;
  readonly roomLabel: string;
  /** General rooms are drawn together in the rail's General card. */
  readonly roomKind: "general" | "workroom";
  readonly pocket?: true | undefined;
  readonly depth?: number | undefined;
  /** The session it sits under, when that one is in the same tidy. */
  readonly parentId?: string | undefined;
}
export type SessionTidyPreview =
  | {
      readonly ok: true;
      readonly count: number;
      readonly words: SessionTidyWords;
      readonly archive: ReadonlyArray<SessionTidyItem>;
    }
  | { readonly ok: false; readonly reason: string };
export type SessionTidySkipped = SessionTidyItem & {
  readonly reason: string;
  readonly why: string;
};
export type SessionTidyOutcome =
  | {
      readonly ok: true;
      readonly archived: ReadonlyArray<SessionTidyItem>;
      /** `why` is the person's words for `reason`. */
      readonly skipped: ReadonlyArray<SessionTidySkipped>;
      readonly remaining: number;
    }
  | { readonly ok: false; readonly reason: string };

/** Where a running tidy stands: the sessions archived so far, in the plan's order. */
export interface SessionTidyProgress {
  readonly archived: ReadonlyArray<SessionTidyItem>;
  readonly total: number | null;
}

export interface SessionTidyInput {
  readonly worldKey: string;
  readonly days: number;
  readonly roomId?: string | undefined;
  /** Every general room: the General card. */
  readonly general?: boolean | undefined;
  /** Every room of one Workroom: its card. */
  readonly workroomKey?: string | undefined;
  /** The person's rail pins: they stay. */
  readonly keep?: ReadonlyArray<string> | undefined;
}

const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

async function post(body: Record<string, unknown>, fetchImpl: typeof fetch) {
  let response: Response;
  try {
    response = await postLocalJson("/api/neucleos/world/sessions/tidy", body, fetchImpl);
  } catch {
    return { ok: false as const, reason: "The tidy response was lost. Refresh and look again." };
  }
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  const value = record(parsed);
  if (!response.ok || value === null || value.ok !== true)
    return {
      ok: false as const,
      reason: value !== null && typeof value.reason === "string" ? value.reason : "tidy-unresolved",
    };
  return { ok: true as const, value };
}

export async function previewSessionTidy(
  input: SessionTidyInput,
  fetchImpl: typeof fetch = fetch,
): Promise<SessionTidyPreview> {
  const answer = await post({ mode: "preview", ...withoutEmpty(input) }, fetchImpl);
  if (!answer.ok) return answer;
  const plan = record(answer.value.plan);
  const words = record(answer.value.words);
  const archive = Array.isArray(plan?.archive) ? (plan.archive as SessionTidyItem[]) : [];
  return {
    ok: true,
    count: archive.length,
    archive,
    words: {
      title: typeof words?.title === "string" ? words.title : "",
      lines: Array.isArray(words?.lines) ? (words.lines as string[]) : [],
    },
  };
}

/**
 * Archive, asking again with the same operation until the Desk says done: a
 * large tidy keeps going on the Desk between answers (sessionTidy.ts
 * SessionTidyRuns). `onProgress` hears each answer on the way.
 */
export async function runSessionTidy(
  input: SessionTidyInput & { readonly operationId: string; readonly count: number },
  fetchImpl: typeof fetch = fetch,
  onProgress?: (progress: SessionTidyProgress) => void,
): Promise<SessionTidyOutcome> {
  for (let attempt = 0; attempt < 400; attempt++) {
    const answer = await post({ mode: "archive", ...withoutEmpty(input) }, fetchImpl);
    if (!answer.ok) return answer;
    const run = record(answer.value.run);
    const archived = Array.isArray(run?.archived) ? (run.archived as SessionTidyItem[]) : [];
    const skipped = Array.isArray(run?.skipped) ? (run.skipped as SessionTidySkipped[]) : [];
    if (run?.done === true)
      return {
        ok: true,
        archived,
        skipped,
        remaining: typeof run.remaining === "number" ? run.remaining : 0,
      };
    onProgress?.({ archived, total: typeof run?.total === "number" ? run.total : null });
  }
  return { ok: false, reason: "The tidy is still running on this Desk. Look again in a while." };
}

/**
 * When this Desk last archived sessions in the World (a tidy, or Cleo's
 * retire_session), ms; null when it has not since it started; undefined when
 * the Desk could not say.
 */
export async function readWorldSessionsChangedAt(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<number | null | undefined> {
  const answer = await post({ mode: "changes", worldKey }, fetchImpl);
  if (!answer.ok) return undefined;
  const at = answer.value.changedAt;
  return typeof at === "number" ? at : at === null ? null : undefined;
}

/**
 * Tell the Desk the sessions pinned in the rail, so Cleo's own tidy keeps the
 * same ones the rail's Tidy up keeps (server railPins.ts). Best effort: until
 * the Desk has heard, Cleo's tidy does not run.
 */
export async function reportRailPins(
  worldKey: string,
  pinnedSessionIds: ReadonlyArray<string>,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const answer = await post({ mode: "pins", worldKey, keep: pinnedSessionIds }, fetchImpl);
  return answer.ok;
}

function withoutEmpty(input: object): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined));
}
